"use client";

import { useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import axios, { AxiosError } from "axios";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import useSWR, { mutate } from "swr";
import {
  ArrowLeftIcon,
  CalendarDaysIcon,
  MapPinIcon,
  ShoppingCartIcon,
} from "@heroicons/react/24/outline";
import { CheckoutDetails } from "@features/checkout/types";
import { Account, Analytics } from "@types";
import { Basket } from "../types";
import { BASE_URL, COUNTRY_CODE } from "@utils/constants";
import { authorizedFetcher, fetcher, handle429s } from "@utils/fetchers";
import {
  formatPrice,
  getCookieConsent,
  getCookieValue,
  joinClassNames,
} from "@utils/helpers";
import {
  useHydrated,
  useLatestCookieValue,
  useSupabaseSession,
  useWindowSize,
} from "@utils/hooks";
import {
  getFirstIncompleteTicketId,
  getRedirectLink,
  isNameRequiredCompleted,
} from "../utils/cartUtils";
import { useSetStickyContinueBar } from "@features/checkout/context/stickyContinueBarContext";
import {
  formatCustomerIOCartEvent,
  getAnalyticsQueryParamsFromCheckoutDetails,
  trackGTMCartEvent,
} from "@utils/analyticsUtils";
import {
  useIsShopsite,
  useRequiresCookieConsent,
} from "@context/DomainContext";
import { DeleteCartDialog } from "@features/deleteCartDialog";
import { useNotificationStore } from "@store/notificationStore";
import LoadingSpinner from "@components/loadingSpinner";
import { analytics } from "@components/analytics";
import { Button, Card, Heading, Text } from "@components/theming";
import CartFee from "../components/cartFee";
import DiscountList from "../components/discountList";
import DisplayTickets from "../components/displayTickets";
import SkeletonLoading from "../components/skeletonLoading";
import UpsellEvents from "../components/upsellEvents";

const CartDetails = ({ isDraftEnabled }: { isDraftEnabled: boolean }) => {
  const locale = useLocale();
  const isShopsite = useIsShopsite();
  const [checkoutClicked, setCheckoutClicked] = useState(false);
  const viewCartTriggered = useRef(false);
  const basketKey = useLatestCookieValue("ASP.NET_SessionId");
  const checkoutTranslations = useTranslations("Checkout.Cart");
  const utilsTranslations = useTranslations("Utils");
  const windowSize = useWindowSize();
  const session = useSupabaseSession();
  const hydrated = useHydrated();
  const requiresCookieConsent = useRequiresCookieConsent();
  const cookieConsent = getCookieConsent();
  const { setFailure } = useNotificationStore((state) => state);
  const websiteUid = getCookieValue("itk-website-uid");
  const router = useRouter();

  const { data: basket, isLoading } = useSWR<Basket>(
    basketKey ? `basket/${basketKey}` : null,
    fetcher
  );

  const {
    data: checkoutDetails,
    isLoading: checkoutLoading,
    error: checkoutError,
    isValidating: checkoutValidating,
  } = useSWR<CheckoutDetails>(
    session && session.access_token && basketKey
      ? [`basket/${basketKey}/checkout`, session.access_token]
      : basketKey && hydrated && !session
      ? `basket/${basketKey}/checkout`
      : null,
    session && session.access_token ? authorizedFetcher : fetcher,
    { onErrorRetry: handle429s }
  );

  const { data: user } = useSWR<
    Account,
    AxiosError,
    [url: string, token: string] | null
  >(
    checkoutDetails && session ? [`account`, session?.access_token] : null,
    authorizedFetcher
  );

  const formattedIds = getAnalyticsQueryParamsFromCheckoutDetails(
    checkoutDetails?.CartList
  );

  const { data: analyticsConfigs } = useSWR<Analytics[]>(
    formattedIds?.formattedEventIds
      ? `analytics?eventIds=${formattedIds.formattedEventIds}`
      : null,
    fetcher
  );

  const canTrack = !requiresCookieConsent || cookieConsent.marketing === true;

  useEffect(() => {
    if (
      checkoutDetails?.CartList &&
      !viewCartTriggered.current &&
      analyticsConfigs &&
      basketKey &&
      canTrack
    ) {
      analytics.track(
        "Cart Viewed",
        formatCustomerIOCartEvent(checkoutDetails, basketKey)
      );
      trackGTMCartEvent(
        checkoutDetails.CartList,
        analyticsConfigs,
        "view_cart",
        checkoutDetails.Currency.CurrencyCode
      );
      viewCartTriggered.current = true;
    }
  }, [checkoutDetails, analyticsConfigs, basketKey, canTrack]);

  function trackStartCheckout() {
    if (checkoutDetails?.CartList && canTrack) {
      analytics.track("Checkout Started", {
        ...formatCustomerIOCartEvent(checkoutDetails, basketKey),
        revenue: checkoutDetails.ItemPriceSum,
        order_id: basketKey,
      });
      if (analyticsConfigs) {
        trackGTMCartEvent(
          checkoutDetails.CartList,
          analyticsConfigs,
          "begin_checkout",
          checkoutDetails.Currency.CurrencyCode
        );
      }
    }
  }

  async function updatePostcode() {
    if (
      !checkoutDetails?.PostCode &&
      checkoutDetails?.PostCodeRequired &&
      session
    ) {
      if (!user?.postcode) return;

      try {
        await axios.patch(
          `${BASE_URL}/legacy/${COUNTRY_CODE}/shop/basket/${basketKey}/checkout/postcode`,
          {
            postCode: user.postcode,
          },
          {
            headers: {
              "itk-website-uid": websiteUid,
            },
          }
        );

        await mutate(`basket/${basketKey}/checkout`);
        await mutate(
          (key: any) =>
            Array.isArray(key) && key[0] === `basket/${basketKey}/checkout`
        );
      } catch (err: AxiosError | any) {
        if (err.response && err.response.status >= 400 && err.status < 500) {
          setFailure(err.response.data.message);
        } else {
          console.error(err);
          setFailure(
            err.response.data.message ??
              checkoutTranslations("somethingwentwrong"),
            true
          );
        }
      }
    }
  }

  const isDesktop = !!windowSize.width && windowSize.width >= 1024;

  // Registered before any early return below so this hook is always called
  // in the same order, regardless of loading/empty-cart state.
  const isCartPageReady =
    !(
      isLoading ||
      checkoutLoading ||
      !hydrated ||
      (!checkoutDetails &&
        checkoutError &&
        checkoutError?.response?.status === 429)
    ) &&
    !!basket &&
    !!basket.Events &&
    basket.Events.length > 0 &&
    !!basketKey;

  // Missing-required-name is intentionally excluded here: the sticky bar
  // jumps to the offending ticket instead of disabling for that case.
  const disableContinueSticky =
    isDraftEnabled || checkoutClicked || checkoutLoading || checkoutValidating;

  const handleContinueClick = async () => {
    if (!basket) return;
    (document.activeElement as HTMLElement)?.blur?.();
    const redirectLink = getRedirectLink(basket, session);
    if (!redirectLink.includes("sign-in")) {
      setCheckoutClicked(true);
    }
    if (
      checkoutDetails &&
      checkoutDetails.RefundProtect.OptedIn === null &&
      checkoutDetails.RefundProtect.QuoteId === null &&
      typeof window !== "undefined"
    ) {
      if (window.sessionStorage.getItem("pulse:s")) {
        window.sessionStorage.removeItem("pulse:s");
      }
      if (window.sessionStorage.getItem("pulse:p")) {
        window.sessionStorage.removeItem("pulse:p");
      }
      if (window.sessionStorage.getItem("pulse:pch")) {
        window.sessionStorage.removeItem("pulse:pch");
      }
      if (window.sessionStorage.getItem("pulse:q")) {
        window.sessionStorage.removeItem("pulse:q");
      }
      if (window.sessionStorage.getItem("pulse:qd")) {
        window.sessionStorage.removeItem("pulse:qd");
      }
    }
    trackStartCheckout();
    await updatePostcode();
    // Below 1024px, skip the sign-in modal intercept and go straight to the
    // full page - router.push still triggers the modal (interception isn't
    // limited to Link clicks), so a hard navigation is needed to bypass it,
    // matching what a direct URL visit or refresh already shows.
    if (redirectLink.includes("sign-in") && !isDesktop) {
      window.location.href = redirectLink;
    } else {
      router.push(redirectLink);
    }
  };

  const firstIncompleteTicketId = basket
    ? getFirstIncompleteTicketId(basket)
    : null;

  const stickyButtonShopsiteClasses =
    "border border-transparent px-4 py-3 !text-base !font-medium shadow-sm focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-white";

  const stickyContinueAction = !basket ? null : firstIncompleteTicketId !==
    null ? (
    <Button
      size="4"
      sharedClasses="w-full gap-2"
      shopsiteClasses={stickyButtonShopsiteClasses}
      href={`#ticket-${firstIncompleteTicketId}`}
    >
      {checkoutTranslations("continue")}
    </Button>
  ) : (
    <Button
      size="4"
      sharedClasses="w-full aria-disabled:cursor-not-allowed gap-2"
      shopsiteClasses={joinClassNames(
        stickyButtonShopsiteClasses,
        "aria-disabled:!bg-indigo-200"
      )}
      id="checkout_link"
      disabled={disableContinueSticky}
      onClick={handleContinueClick}
    >
      {checkoutClicked ? (
        <>
          <LoadingSpinner
            size="18"
            color={isShopsite ? "#ffffff" : "var(--gray-11)"}
          />
          <span className="w-max">{checkoutTranslations("checkingout")}</span>
        </>
      ) : (
        checkoutTranslations("continue")
      )}
    </Button>
  );

  useSetStickyContinueBar(
    isCartPageReady
      ? {
          action: stickyContinueAction,
          secondary: {
            id: "clearCart",
            content: (
              <div className="px-4 pb-2">
                <DeleteCartDialog fullWidth />
              </div>
            ),
          },
        }
      : null
  );

  if (
    isLoading ||
    checkoutLoading ||
    !hydrated ||
    (!checkoutDetails &&
      checkoutError &&
      checkoutError?.response?.status === 429)
  )
    return (
      <div className="relative">
        <SkeletonLoading />
        {checkoutError?.response?.status === 429 ? (
          <Card
            shopsiteClasses="bg-white border border-gray-200 rounded-lg"
            sharedClasses="absolute top-20 left-1/2 -translate-x-1/2 w-[40rem] max-w-[90dvw]"
          >
            <div className="flex flex-col items-center justify-center gap-1 w-full h-[197px]">
              <Heading
                as="h3"
                size="6"
                weight="bold"
                sharedClasses="text-center mx-4"
                shopsiteClasses="font-bold text-2xl"
              >
                {utilsTranslations("429s.heading")}
              </Heading>
              <Text
                size="5"
                weight="bold"
                whitelabelClasses="text-[color:var(--accent-11)]"
                sharedClasses="text-center"
                shopsiteClasses="text-base text-gray-500"
              >
                {utilsTranslations("429s.description")}
              </Text>
            </div>
          </Card>
        ) : (
          <></>
        )}
      </div>
    );

  if (!basket || !basket.Events || basket.Events.length === 0 || !basketKey) {
    return (
      <div
        data-testid="emptycart"
        className="w-full flex flex-col items-center gap-4 sm:p-4 sm:py-8"
      >
        <Heading
          as="h3"
          size={{ initial: "7", md: "9" }}
          weight="bold"
          align="center"
          shopsiteClasses="text-3xl sm:text-7xl text-center font-black tracking-tight text-black"
        >
          {checkoutTranslations("empty.heading")}
        </Heading>
        <ShoppingCartIcon className="w-28 h-28" />
        <Text
          as="p"
          size="4"
          color="gray"
          align="center"
          shopsiteClasses="text-gray-600 text-lg text-center"
        >
          {checkoutTranslations("empty.description")}
        </Text>
        <Button size="3" href="/" shopsiteClasses="px-8 py-2 !rounded-lg">
          {checkoutTranslations("empty.shopnow")}
        </Button>
      </div>
    );
  }

  const has_other_events =
    basket.Events.map((event) => event.Showings).flat().length > 1;

  // Exact original logic, kept for the unchanged desktop inline button
  // (which still just disables on a missing required name, rather than
  // jumping to it like the mobile sticky bar does).
  const disableContinueDesktop =
    isDraftEnabled ||
    checkoutClicked ||
    !isNameRequiredCompleted(basket) ||
    checkoutLoading ||
    checkoutValidating;

  return (
    <div
      data-testid="cartcomponent"
      className={joinClassNames(
        "space-y-5 sm:space-y-10",
        !hydrated ? "pointer-events-none" : ""
      )}
    >
      {basket.Events.map((event) =>
        event.Showings.map((showing) => (
          <div key={`showings-${event.Id}-${showing.Id}`}>
            {windowSize.width && windowSize.width > 640 ? (
              <div className="relative h-40 box-content flex flex-row items-start">
                <div
                  className={joinClassNames(
                    "flex-shrink-0 overflow-hidden h-full w-40",
                    isShopsite ? "rounded-lg" : "rounded-[var(--radius-3)]"
                  )}
                >
                  <Link
                    href={`/events/${
                      event.Url.split("/events/")[1]
                    }?view=tickets`}
                  >
                    <Image
                      src={
                        locale === "de"
                          ? event.PortraitImageUrl.replace(
                              /\.(?!.*\.)/, // last occurance of "." in the string
                              "_de."
                            ).trim()
                          : event.PortraitImageUrl.trim()
                      }
                      alt={event.Name}
                      width={160}
                      height={160}
                    />
                  </Link>
                </div>
                <div className="ml-6 flex flex-col justify-between w-full h-full">
                  <div className="flex justify-between">
                    <Link
                      href={`/events/${
                        event.Url.split("/events/")[1]
                      }?view=tickets`}
                    >
                      <Heading
                        as="h2"
                        size={{ initial: "6", md: "7" }}
                        weight="bold"
                        shopsiteClasses="text-2xl font-bold lg:text-3xl lg:truncate lg:max-w-2xl text-black"
                      >
                        {event.Name}
                      </Heading>
                    </Link>
                    <Text
                      as="p"
                      sharedClasses="text-2xl font-bold lg:text-3xl lg:truncate sm:block"
                    >
                      {formatPrice(
                        locale,
                        basket.Currency.CurrencyCode,
                        showing.Subtotal
                      )}
                    </Text>
                  </div>
                  <div
                    className={joinClassNames(
                      "mt-auto",
                      isShopsite
                        ? "text-gray-500"
                        : "text-[color:var(--gray-11)]"
                    )}
                  >
                    <div className="flex items-center mt-2">
                      <CalendarDaysIcon className="shrink-0 mr-1.5 h-5 w-5" />
                      <p className="text-base block">{showing.Date}</p>
                    </div>
                    <div className="flex items-center mt-2">
                      <MapPinIcon className="shrink-0 mr-1.5 h-5 w-5" />
                      <p className="text-base block">
                        {showing.Venue}, {showing.Region}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2 items-start rounded-lg">
                <div className="relative flex gap-2 items-center justify-between flex-1 w-full">
                  <Link
                    href={`/events/${
                      event.Url.split("/events/")[1]
                    }?view=tickets`}
                  >
                    <Heading
                      as="h2"
                      sharedClasses="text-xl leading-tight font-bold"
                    >
                      {event.Name}
                    </Heading>
                  </Link>
                  <Text as="p" sharedClasses="text-lg leading-tight">
                    {formatPrice(
                      locale,
                      basket.Currency.CurrencyCode,
                      showing.Subtotal
                    )}
                  </Text>
                </div>
                <div className="flex flex-col gap-1">
                  <div className="flex items-center">
                    <CalendarDaysIcon className="shrink-0 mr-1.5 h-5 w-5" />
                    <Text
                      as="p"
                      color="gray"
                      sharedClasses="text-base block"
                      shopsiteClasses="text-gray-500"
                    >
                      {showing.Date}
                    </Text>
                  </div>
                  <div className="flex items-center">
                    <MapPinIcon className="shrink-0 mr-1.5 h-5 w-5" />
                    <Text
                      as="p"
                      color="gray"
                      sharedClasses="text-base block"
                      shopsiteClasses="text-gray-500"
                    >
                      {showing.Venue}, {showing.Region}
                    </Text>
                  </div>
                </div>
              </div>
            )}
            <DisplayTickets
              items={showing.Items}
              comboItems={showing.ComboItems}
              hasOtherEvents={has_other_events}
              currency={basket.Currency}
              eventName={event.Name}
              eventId={event.Id}
              showingId={showing.Id}
              analyticsConfigs={
                analyticsConfigs && canTrack
                  ? analyticsConfigs.filter(
                      (config) => config.eventId === event.Id
                    )
                  : []
              }
              canTrack={canTrack}
            />
          </div>
        ))
      )}
      {basket.Discounts.length > 0 && (
        <DiscountList discounts={basket.Discounts} />
      )}
      <div className="flex justify-center">
        <div className="w-full max-w-2xl">
          <CartFee
            checkoutDetails={checkoutDetails}
            isLoading={checkoutLoading || checkoutValidating}
          />
          <div className="flex items-center max-sm:flex-col-reverse sm:justify-between w-full">
            <div className="relative flex items-center mt-6 text-center text-sm">
              <Link
                href={"/"}
                className={joinClassNames(
                  "peer font-medium",
                  isShopsite
                    ? "text-indigo-600 hover:text-indigo-500"
                    : "text-[color:var(--accent-11)] hover:text-[color:var(--accent-10)]"
                )}
              >
                {checkoutTranslations("continueshopping")}
              </Link>
              <ArrowLeftIcon
                className={joinClassNames(
                  "absolute right-full h-4 w-4 opacity-0 peer-hover:opacity-100 translate-x-2 peer-hover:-translate-x-1 transition-all ease-in-out duration-200 pointer-events-none",
                  isShopsite
                    ? "text-indigo-500"
                    : "text-[color:var(--accent-10)]"
                )}
              />
            </div>
            <div className="mt-6 flex items-center flex-col max-sm:w-full max-sm:gap-y-2 sm:flex-row sm:gap-x-2">
              {isDesktop && <DeleteCartDialog />}
              {isDesktop && (
                <Button
                  size="3"
                  sharedClasses="max-sm:w-full aria-disabled:cursor-not-allowed gap-2"
                  shopsiteClasses="aria-disabled:!bg-indigo-200 px-4"
                  id="checkout_link"
                  disabled={disableContinueDesktop}
                  onClick={handleContinueClick}
                >
                  {checkoutClicked ? (
                    <>
                      <LoadingSpinner
                        size="18"
                        color={isShopsite ? "#ffffff" : "var(--gray-11)"}
                      />
                      <span className="w-max">
                        {checkoutTranslations("checkingout")}
                      </span>
                    </>
                  ) : (
                    checkoutTranslations("continue")
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      {basket.Upsells.length > 0 && <UpsellEvents events={basket.Upsells} />}
    </div>
  );
};

export default CartDetails;
